 import { useState } from "react";
 import {
   Collapsible,
   CollapsibleContent,
   CollapsibleTrigger,
 } from "@/components/ui/collapsible";
 import { ChevronDown, PieChart, Info } from "lucide-react";
 import { formatCurrency, formatPercent } from "@/lib/pricing-calculations";
 import { cn } from "@/lib/utils";
 import { Skeleton } from "@/components/ui/skeleton";
 
 interface OperationalCostsCardProps {
   avgCardFeePercent: number;
   avgFixedExpensePercent: number;
   currentPrice: number;
   fixedExpensePerUnit: number;
   estimatedOperationalCost: number;
   estimatedProfit: number;
   estimatedProfitPercent: number;
   isLoading?: boolean;
 }
 
 export function OperationalCostsCard({
   avgCardFeePercent,
   avgFixedExpensePercent,
   currentPrice,
   fixedExpensePerUnit,
   estimatedOperationalCost,
   estimatedProfit,
   estimatedProfitPercent,
   isLoading,
 }: OperationalCostsCardProps) {
   const [isOpen, setIsOpen] = useState(false);
 
   const cardFeeValue = currentPrice * (avgCardFeePercent / 100);
 
   if (isLoading) {
     return (
       <div className="p-3 bg-muted/50 rounded-lg">
         <div className="flex items-center gap-2">
           <PieChart className="h-4 w-4 text-muted-foreground" />
           <Skeleton className="h-4 w-40" />
         </div>
       </div>
     );
   }
 
   return (
     <Collapsible open={isOpen} onOpenChange={setIsOpen}>
       <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
         <div className="flex items-center gap-2">
           <PieChart className="h-4 w-4 text-muted-foreground" />
           <span className="font-medium text-sm">Custos Operacionais</span>
           <span className="text-xs text-muted-foreground">(informativo)</span>
         </div>
         <ChevronDown 
           className={cn(
             "h-4 w-4 text-muted-foreground transition-transform",
             isOpen && "rotate-180"
           )} 
         />
       </CollapsibleTrigger>
       <CollapsibleContent>
         <div className="p-3 pt-2 space-y-3">
           <p className="text-xs text-muted-foreground">
             Baseado nos dados dos últimos 30 dias:
           </p>
 
           {/* Taxas detalhadas */}
           <div className="space-y-2">
             <div className="flex justify-between items-center text-sm">
               <span className="text-muted-foreground">Taxa Média de Cartão:</span>
               <span className="font-medium">
                 {formatPercent(avgCardFeePercent)} 
                 <span className="text-muted-foreground ml-1">
                   ({formatCurrency(cardFeeValue)} neste prato)
                 </span>
               </span>
             </div>
             <div className="flex justify-between items-center text-sm">
               <span className="text-muted-foreground">Despesas Fixas Médias:</span>
               <span className="font-medium">
                 {formatPercent(avgFixedExpensePercent)}
                 <span className="text-muted-foreground ml-1">
                   ({formatCurrency(fixedExpensePerUnit)} rateado)
                 </span>
               </span>
             </div>
           </div>
 
           {/* Separador */}
           <div className="border-t pt-3 space-y-2">
             <div className="flex justify-between items-center text-sm">
               <span className="text-muted-foreground">Custo Operacional Total:</span>
               <span className="font-bold text-foreground">
                 {formatCurrency(estimatedOperationalCost)}
               </span>
             </div>
             <div className="flex justify-between items-center text-sm">
               <span className="text-muted-foreground">Lucro Estimado por Prato:</span>
               <span className={cn(
                 "font-bold",
                estimatedProfit >= 0 ? "text-primary" : "text-destructive"
               )}>
                 {formatCurrency(estimatedProfit)}
               </span>
             </div>
             <div className="flex justify-between items-center text-sm">
               <span className="text-muted-foreground">Lucro Líquido Estimado (%):</span>
               <span className={cn(
                 "font-bold",
                estimatedProfitPercent >= 0 ? "text-primary" : "text-destructive"
               )}>
                 {formatPercent(estimatedProfitPercent)}
               </span>
             </div>
           </div>
 
           {/* Aviso informativo */}
          <div className="flex items-start gap-2 p-2 bg-muted rounded-lg text-xs text-muted-foreground">
             <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
             <span>Esses valores são estimativas baseadas nas médias do período.</span>
           </div>
         </div>
       </CollapsibleContent>
     </Collapsible>
   );
 }